import {
	type DefaultMessagesResponse,
	type PlacementMessageFrontendFactory,
	type AnyFrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';
import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';
import { createPlacementBackend } from '@post-office/message-maker-node/placements/create-placement-backend';
import { token } from '@atlaskit/tokens';
import type { FlagProps } from '@atlaskit/flag/types';
import { css } from '@compiled/react';
import type { NextBestAction } from '@post-office/confluence-next-best-action';
import {
	type BackendMessageBody,
	type PlacementMessageBackendFactory,
} from '@post-office/message-maker-node/placements/types';
import { createDefaultPlacementMiddleware } from '@post-office/message-maker-node/placements/middleware';
import type { SerializableRecord } from '@post-office/serializable-record';
import { usePlacementApi } from '@post-office/use-placement-api';
import {
	type ComponentType,
	type ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import {
	TEMPLATE_XFLOW_EXPERIMENT_KEY,
	type PostOfficeSideNavMessageKey,
	usePostOfficeSideNavMessages,
} from './contexts/PostOfficeSideNavMessageProvider';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { EXPERIMENTAL_MESSAGES, NOT_ENROLLED_COHORT } from './experimental-message-list';
import type { AnyPayload } from '@post-office/message-template-contracts/placement-v2';
import {
	type BackendSyncMessageStage,
	type AnyBackendMessageStage,
} from '@post-office/message-maker-node/placements/types';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import { runConfluenceSideNavPlacement } from './run-confluence-side-nav-placement';

const placementConfig: PlacementBrowserConfig = {
	placementId: 'confluence-side-nav',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
	},
};

const { createFrontend, createBackend } = {
	createFrontend: createPlacementFrontend(placementConfig),
	createBackend: createPlacementBackend(placementConfig),
};

export const PlacementBackend: PlacementMessageBackendFactory<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = createBackend({
	customMiddleware: createDefaultPlacementMiddleware(runConfluenceSideNavPlacement),
});

// Props passed from Product to Placement
// These types intentionally mirror Confluence's flags API instead of importing it here.
// That import would reverse the Product -> Placement dependency and risk creating a cycle.
export type PlacementFlagDescriptor = Partial<
	Pick<
		FlagProps,
		| 'id'
		| 'title'
		| 'description'
		| 'actions'
		| 'appearance'
		| 'delayAnnouncement'
		| 'autoDismissSeconds'
	>
> & {
	type?: 'success-circle' | 'success' | 'info' | 'error' | 'warning' | 'custom';
	customIcon?: ReactElement;
	customComponent?: ComponentType<FlagProps>;
	close?: 'auto' | 'never';
	onShown?: () => void;
	onClose?: () => void;
};

type PlacementShowFlagArgs = Pick<
	PlacementFlagDescriptor,
	| 'id'
	| 'title'
	| 'description'
	| 'actions'
	| 'onClose'
	| 'appearance'
	| 'delayAnnouncement'
	| 'autoDismissSeconds'
> & {
	isAutoDismiss?: boolean;
	customIcon?: ReactElement;
};

export type PlacementFlags = {
	showMultipleFlags: (flags: Omit<PlacementFlagDescriptor, 'id'>[]) => Promise<void>;
	showFlag: (flag: PlacementFlagDescriptor) => Promise<unknown>;
	updateFlag: (id: FlagProps['id'] | undefined, patch: PlacementShowFlagArgs) => Promise<void>;
	hideFlag: (id: FlagProps['id'] | undefined) => Promise<void>;
	hideAllFlags: () => Promise<void>;
	setFlagsVisibility: (areFlagsVisible: boolean) => Promise<void>;
	showSuccessFlag: (flag: PlacementShowFlagArgs) => Promise<unknown>;
	showSuccessCircleFlag: (flag: PlacementShowFlagArgs) => Promise<unknown>;
	showInfoFlag: (flag: PlacementShowFlagArgs) => Promise<unknown>;
	showCustomFlag: (flag: PlacementShowFlagArgs) => Promise<unknown>;
	showWarningFlag: (flag: PlacementShowFlagArgs) => Promise<unknown>;
	showErrorFlag: (flag: PlacementShowFlagArgs) => Promise<unknown>;
};

export type PlacementFrontendProps = {
	preloadedInitialData: DefaultMessagesResponse | undefined;
	onDismiss: (messages: { messages: BackendMessageBody<SerializableRecord>[] }) => void;
	onSideNavClick: (
		nextBestActionType: NextBestAction,
		preloadedMessageState: { messages: BackendMessageBody<SerializableRecord>[] },
	) => void;
	onCallback: (callbackKey: NextBestAction) => Promise<void>;
	flags?: PlacementFlags;
	currentRoute?: string;
};

const confluenceSideNavStyles = css({
	display: 'flex',
	margin: token('space.150', '12px'),
});

const SORTED_MESSAGE_KEYS: PostOfficeSideNavMessageKey[] = [
	'MCP_SETUP_NUDGE',
	'CONNECT_3P_TOOLS_NUDGE',
	'CREATE_WITH_ROVO_NUDGE',
	'CREATE_SPACE',
	'CREATE_PAGE',
	'CREATE_WHITEBOARD',
	'1_ON_1_LOOM_NUDGE',
	'LOOM_AI_MEETING_NOTES',
	TEMPLATE_XFLOW_EXPERIMENT_KEY,
	'RESURRECTED_USER_NUDGE',
	'ROVO_EXTENSION_CTA',
];

const TEMPLATE_XFLOW_GATE = 'twcg-2112-twc-xflow-conf-pages-templates-gate';

const getMessageSortKey = (
	message: BackendMessageBody<SerializableRecord>,
): PostOfficeSideNavMessageKey => {
	// @ts-expect-error Property 'experimentKey' does not exist on type 'BackendMessageBody<SerializableRecord>'
	if (message.experimentKey === TEMPLATE_XFLOW_EXPERIMENT_KEY) {
		return TEMPLATE_XFLOW_EXPERIMENT_KEY;
	}

	// @ts-expect-error Property 'nextBestActionType' does not exist on type 'BackendMessageBody<SerializableRecord>'
	return message.nextBestActionType as NextBestAction;
};

// todo: Resolve backend ordering incorrectly
const sortMessages = (messages: BackendMessageBody<SerializableRecord>[] = []) => {
	return [...messages].sort((a, b) => {
		return (
			SORTED_MESSAGE_KEYS.indexOf(getMessageSortKey(a)) -
			SORTED_MESSAGE_KEYS.indexOf(getMessageSortKey(b))
		);
	});
};

type DisplayedMessage = {
	message: BackendMessageBody<SerializableRecord>;
	experimentCohort?: string;
};

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	PlacementFrontendProps
> = createFrontend(
	(Messages) =>
		({
			preloadedInitialData,
			onDismiss,
			onSideNavClick,
			onCallback,
			flags,
			currentRoute,
		}: PlacementFrontendProps) => {
			const [{ messages, status }, setMessageState] = useState<{
				messages: BackendMessageBody<SerializableRecord>[] | undefined;
				status: 'loading' | 'done';
			}>(() => {
				if (preloadedInitialData) {
					return preloadedInitialData?.messages?.length > 0
						? {
								status: 'done',
								messages: getMessages(preloadedInitialData?.messages),
							}
						: { messages: undefined, status: 'done' };
				}

				return { messages: undefined, status: 'loading' };
			});

			const context = usePostOfficeSideNavMessages();

			const isDone = status === 'done';

			const { data, error, isLoading } = usePlacementApi<DefaultMessagesResponse>('/', {
				autoRefetch: {
					isEnabled: !isDone,
					updateKey: 'confluence-side-nav',
				},
			});

			const handleOnDismissed = useCallback(() => {
				// no more messages until the next fetch
				onDismiss({ messages: [] });

				setMessageState({ messages: undefined, status: 'done' });
			}, [onDismiss]);

			const handleOnSideNavClick = useCallback(
				(nextBestActionType: NextBestAction) => {
					// display the next message
					setMessageState((prevState) => {
						if (prevState.messages) {
							const newMessages = prevState.messages.slice(1);

							onSideNavClick(nextBestActionType, { messages: newMessages });

							return {
								messages: newMessages,
								status: 'done',
							};
						}

						onSideNavClick(nextBestActionType, { messages: [] });

						return prevState;
					});
				},
				[onSideNavClick],
			);

			useEffect(() => {
				if (!isLoading && !isDone) {
					setMessageState({
						messages: getMessages(data?.messages),
						status: 'done',
					});
				}
			}, [isLoading, isDone, data]);

			const displayedMessage = useMemo<DisplayedMessage | undefined>(() => {
				if (!messages?.length) {
					return;
				}

				const filteredMessages = messages.filter((message) => {
					// @ts-expect-error Property 'explicitEnableRequired' does not exist on type 'BackendMessageBody<SerializableRecord>'
					if (message.explicitEnableRequired as boolean) {
						const isTemplateXflowMessage =
							// @ts-expect-error Property 'experimentKey' does not exist on type 'BackendMessageBody<SerializableRecord>'
							message.experimentKey === TEMPLATE_XFLOW_EXPERIMENT_KEY;
						const explicitMessageKey = isTemplateXflowMessage
							? TEMPLATE_XFLOW_EXPERIMENT_KEY
							: // @ts-expect-error Property 'nextBestActionType' does not exist on type 'BackendMessageBody<SerializableRecord>'
								(message.nextBestActionType as NextBestAction);
						const isExplicitlyEnabled = context?.enabledMessages.includes(explicitMessageKey);

						if (isTemplateXflowMessage) {
							// @ts-expect-error Property 'tapReminderTrait' does not exist on type 'BackendMessageBody<SerializableRecord>'
							const hasReminderTrait = message.tapReminderTrait === 'remind';

							return (
								FeatureGates.checkGate(TEMPLATE_XFLOW_GATE) &&
								(hasReminderTrait || isExplicitlyEnabled)
							);
						}

						return isExplicitlyEnabled;
					}

					if (message.messageTemplateId === 'rovo-extension-install-cta-left-nav') {
						// Don't show on FPS to avoid collision with existing install CTA (built outside of PO)
						if (currentRoute?.includes('/search')) {
							return false;
						}
					}

					return true;
				});

				if (!filteredMessages.length) {
					return;
				}

				const explicitlyEnabledTemplateXflowMessage = context?.enabledMessages.includes(
					TEMPLATE_XFLOW_EXPERIMENT_KEY,
				)
					? filteredMessages.find(
							(message) =>
								// @ts-expect-error Property 'experimentKey' does not exist on type 'BackendMessageBody<SerializableRecord>'
								message.experimentKey === TEMPLATE_XFLOW_EXPERIMENT_KEY,
						)
					: undefined;

				if (explicitlyEnabledTemplateXflowMessage) {
					return { message: explicitlyEnabledTemplateXflowMessage };
				}

				// https://switcheroo.atlassian.com/ui/gates/f775d661-abc2-488d-b58c-c28fab9bd58f/key/po_enable_confluence-side-nav-exp-messages
				if (FeatureGates.checkGate('po_enable_confluence-side-nav-exp-messages')) {
					for (const message of filteredMessages) {
						const experimentalMessage = EXPERIMENTAL_MESSAGES.find(
							(msg) => msg.messageTemplateId === message.messageTemplateId,
						);

						// Non-experimental messages are always eligible and should not be overridden by
						// later experimental messages in the order they are defined.
						if (!experimentalMessage) {
							return { message };
						}

						if (
							experimentalMessage.shouldProceedToStatsigEvaluation &&
							!experimentalMessage.shouldProceedToStatsigEvaluation()
						) {
							// This experimental message should not proceed to Statsig evaluation in the
							// current environment – skip it and continue checking later messages.
							continue;
						}

						const experimentCohort = FeatureGates.getExperimentValue<string>(
							experimentalMessage.statsigExperiment,
							experimentalMessage.statsigParameterName,
							NOT_ENROLLED_COHORT,
						);

						if (experimentalMessage.statsigTestCohortPredicate(experimentCohort)) {
							return {
								message,
								experimentCohort,
							};
						}
					}

					return undefined;
				}

				return filteredMessages[0] ? { message: filteredMessages[0] } : undefined;
			}, [messages, context, currentRoute]);

			if (error) {
				return null;
			}

			if (status === 'loading') {
				return null;
			}

			if (!displayedMessage) {
				return <div data-empty-side-nav-placement />;
			}

			const { message, experimentCohort } = displayedMessage;

			return (
				<div
					css={
						!FeatureGates.checkGate('navx-global-side-nav-confluence') && confluenceSideNavStyles
					}
				>
					<Messages.render
						{...message}
						experimentCohort={experimentCohort}
						onSideNavClick={handleOnSideNavClick}
						handleOnDismissed={handleOnDismissed}
						onCallback={onCallback}
						flags={flags}
					/>
				</div>
			);
		},
);

// Trust the result of backend ranking, not a frontend gate or another experiment read.
// Missing metadata (control, fallback or an older response) keeps today's heuristic.
const getMessages = (
	messages: BackendMessageBody<SerializableRecord>[] = [],
): BackendMessageBody<SerializableRecord>[] => {
	if (messages.every((message) => 'rankingMode' in message && message.rankingMode === 'server')) {
		return messages;
	}
	return sortMessages(messages);
};
